import { useEffect, useRef, useCallback } from "react";

interface Props {
    hasMore: boolean;
    loading: boolean;
    onLoadMore?: () => void; // <-- cho phép undefined khi tắt load
    scrollRootRef?: React.RefObject<Element>;
    threshold?: number;
    rootMargin?: string;
}

export const useInfiniteScroll = ({
    hasMore,
    loading,
    onLoadMore,
    scrollRootRef,
    threshold = 0.5,
    rootMargin = "0px",
}: Props) => {
    const observer = useRef<IntersectionObserver | null>(null);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const hasMountedRef = useRef(false);
    const isInitialMount = useRef(true); // ✅ Track initial mount riêng

    const setLoaderRef = useCallback((node: HTMLDivElement | null) => {
        if (loaderRef.current && observer.current) {
            observer.current.unobserve(loaderRef.current);
        }
        loaderRef.current = node;
        if (node && observer.current) {
            observer.current.observe(node);
        }
    }, []);

    useEffect(() => {
        const root = scrollRootRef?.current || null;
        const loader = loaderRef.current;

        if (!loader || loading || !hasMore || !onLoadMore) {
            observer.current?.disconnect();
            return;
        }

        if (!("IntersectionObserver" in window)) {
            const handleScroll = () => {
                const scrollRoot = root instanceof Element ? root : window;
                const scrollTop =
                    scrollRoot === window
                        ? window.scrollY || document.documentElement.scrollTop
                        : (scrollRoot as Element).scrollTop;

                const scrollHeight =
                    scrollRoot === window
                        ? document.documentElement.scrollHeight
                        : (scrollRoot as Element).scrollHeight;

                const clientHeight =
                    scrollRoot === window
                        ? window.innerHeight
                        : (scrollRoot as Element).clientHeight;

                if (
                    scrollTop + clientHeight >= scrollHeight - 10 &&
                    hasMore &&
                    !loading &&
                    onLoadMore
                ) {
                    onLoadMore();
                }
            };

            const scrollRoot = root instanceof Element ? root : window;
            scrollRoot.addEventListener("scroll", handleScroll);
            return () => scrollRoot.removeEventListener("scroll", handleScroll);
        }

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(
            ([entry]) => {
                // ✅ Bỏ qua lần intersect đầu tiên khi mount
                if (isInitialMount.current) {
                    isInitialMount.current = false;
                    return;
                }

                if (entry.isIntersecting && hasMore && !loading && onLoadMore) {
                    onLoadMore();
                }
            },
            {
                root,
                threshold,
                rootMargin,
            }
        );

        if (loader) observer.current.observe(loader);

        return () => {
            observer.current?.disconnect();
            isInitialMount.current = true; // ✅ Reset khi unmount
        };
    }, [hasMore, loading, onLoadMore, scrollRootRef?.current, threshold, rootMargin]);

    return { setLoaderRef };
};